import { LanguageCode } from '@/gql/graphql'

export interface SupportedLanguage {
  code: LanguageCode
  label: string
  shortLabel: string
}

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  { code: LanguageCode.Pt, label: 'Português', shortLabel: 'PT' },
  { code: LanguageCode.En, label: 'English', shortLabel: 'EN' },
  { code: LanguageCode.Es, label: 'Español', shortLabel: 'ES' },
  { code: LanguageCode.Fr, label: 'Français', shortLabel: 'FR' },
]

export const FALLBACK_LANGUAGE_CODE = LanguageCode.Pt

export interface SwitcherLanguageOption {
  vendureCode: string
  catalogCode: LanguageCode
  label: string
  shortLabel: string
}

export function baseSubtag(vendureCode: string): string {
  return vendureCode.split('_')[0]
}

export function getAvailableSwitcherLanguages(
  availableLanguageCodes: readonly string[] | null | undefined
): SwitcherLanguageOption[] {
  if (!availableLanguageCodes?.length) return []

  const seen = new Set<LanguageCode>()
  const result: SwitcherLanguageOption[] = []

  for (const vendureCode of availableLanguageCodes) {
    const match = SUPPORTED_LANGUAGES.find((l) => l.code === baseSubtag(vendureCode))
    if (match && !seen.has(match.code)) {
      seen.add(match.code)
      result.push({ vendureCode, catalogCode: match.code, label: match.label, shortLabel: match.shortLabel })
    }
  }

  return result
}

export function toCatalogCode(vendureCode: string | null | undefined): LanguageCode {
  if (!vendureCode) return FALLBACK_LANGUAGE_CODE
  const match = SUPPORTED_LANGUAGES.find((l) => l.code === baseSubtag(vendureCode))
  return match ? match.code : FALLBACK_LANGUAGE_CODE
}

/** URL prefix segment for a Vendure language code: `pt_BR` → `pt`. */
export function toUrlLang(vendureCode: string | null | undefined): string {
  return toCatalogCode(vendureCode)
}

/** True when `segment` is a language prefix this storefront can route (`/pt/...`, `/en/...`). */
export function isUrlLang(segment: string | null | undefined): boolean {
  return !!segment && SUPPORTED_LANGUAGES.some((l) => l.code === segment)
}

/**
 * Resolves a URL prefix to one of the channel's Vendure language codes, or null
 * when the channel does not serve that language.
 */
export function fromUrlLang(
  urlLang: string | null | undefined,
  availableLanguageCodes: readonly string[] | null | undefined
): string | null {
  if (!isUrlLang(urlLang)) return null
  const match = getAvailableSwitcherLanguages(availableLanguageCodes).find((o) => o.catalogCode === urlLang)
  return match ? match.vendureCode : null
}
